<template>
  <div class="fade-in bg-white rounded-xl p-4 flex items-center justify-between w-full h-16 max-w-md">
      <Select v-model="selectedWeekRef" @update:model-value="handleWeekSelect">
        <SelectTrigger class="min-w-64 pl-3">
          <div class="flex items-center justify-between w-full text-lg font-medium">
            <LoaderCircle v-if="expeditionsStore.isLoading" class="h-4 w-4 animate-spin ml-2" />
            <SelectValue v-else :placeholder="placeholderText" />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="week in availableWeeks"
            :key="week"
            :value="week"
            :class="{ 'font-medium': week === actualCurrentWeekNumber?.toString() }"
          >
            Week {{ week }}
            <span v-if="week === actualCurrentWeekNumber?.toString()" class="ml-1 text-gray-500 text-sm">(Current)</span>
          </SelectItem>
        </SelectContent>
      </Select>
    <div class="flex items-center space-x-5 ml-5">
      <button
class="text-black p-2 rounded-full"
        :disabled="isPrevDisabled || expeditionsStore.isLoading"
        :class="{ 'opacity-30': isPrevDisabled || expeditionsStore.isLoading, 'hover:bg-gray-100': !isPrevDisabled && !expeditionsStore.isLoading }"
        @click="navigateToPreviousWeek"
      >
        <ChevronLeft class="w-5 h-5" />
      </button>
      <button
class="text-black p-2 rounded-full"
        :disabled="isNextDisabled || expeditionsStore.isLoading"
        :class="{ 'opacity-30': isNextDisabled || expeditionsStore.isLoading, 'hover:bg-gray-100': !isNextDisabled && !expeditionsStore.isLoading }"
        @click="navigateToNextWeek"
      >
        <ChevronRight class="w-5 h-5" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ChevronLeft, ChevronRight, LoaderCircle } from 'lucide-vue-next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useExpeditionsStore } from '@/stores/expeditions';
import { useRouter, useRoute } from 'vue-router';

const props = defineProps<{
  currentWeek: number | null;
  selectedWeek: number | null;
  expeditionId: string | number;
  userId: string | number;
  availableWeeks?: number[];
}>();

const router = useRouter();
const route = useRoute();

const expeditionsStore = useExpeditionsStore();

const selectedWeekRef = ref<string | null>(props.selectedWeek?.toString() ?? null);

// Watch for changes in the selectedWeek prop
watch(() => props.selectedWeek, (newSelectedWeek) => {
  if (newSelectedWeek !== null && newSelectedWeek !== undefined) {
    selectedWeekRef.value = newSelectedWeek.toString();
  } else {
    selectedWeekRef.value = null;
  }
}, { immediate: true });

const actualCurrentWeekNumber = computed(() => props.currentWeek);

const MAX_FUTURE_WEEKS = computed(() => {
  if (expeditionsStore.adventurerWeeklyProgress?.preview_weeks) {
    return expeditionsStore.adventurerWeeklyProgress?.preview_weeks - 1
  }
  return 3
});

const availableWeeks = computed(() => {
  // If available weeks are provided, use them
  if (props.availableWeeks && props.availableWeeks.length > 0) {
    return props.availableWeeks.map(week => week.toString()).sort((a, b) => parseInt(a) - parseInt(b));
  }
  
  const current = props.currentWeek;
  if (current === undefined || current === null) {
    return [];
  }

  const totalWeeks = expeditionsStore.adventurerWeeklyProgress?.total_weeks;
  const maxPreviewWeek = current + MAX_FUTURE_WEEKS.value;

  const lastWeekToShow = totalWeeks ? Math.min(maxPreviewWeek, totalWeeks) : maxPreviewWeek;
  
  const weeks = Array.from({ length: lastWeekToShow }, (_, i) => (i + 1).toString());
  return weeks;
});

const allPossibleWeeksSorted = computed(() => {
  return availableWeeks.value.map(week => parseInt(week));
});


const emit = defineEmits(['week-selected']);

const handleWeekSelect = async (weekValue: any) => {
  const weekString = typeof weekValue === 'number' ? weekValue.toString() : (typeof weekValue === 'string' ? weekValue : null);

  if (!weekString || !props.expeditionId || !props.userId) {
    if (weekValue !== null && weekValue !== undefined) {
        console.warn('Unexpected value type from Select update:', typeof weekValue, weekValue);
    }
    return;
  }

  selectedWeekRef.value = weekString;
  emit('week-selected', weekString);

  try {
    await expeditionsStore.fetchAdventurerProgressByWeek(
      String(props.expeditionId),
      String(props.userId),
      weekString
    );
  } catch (error) {
    console.error('Error fetching progress for week:', weekString, error);
  }
};

const placeholderText = computed(() => {
  if (selectedWeekRef.value) {
    const weekNum = parseInt(selectedWeekRef.value);
    if (weekNum === props.currentWeek) {
      return `Week ${selectedWeekRef.value} (Current)`;
    }
    return `Week ${selectedWeekRef.value}`;
  }
  return 'Select a week';
});

const currentlySelectedWeekNumeric = computed(() => {
  return selectedWeekRef.value ? parseInt(selectedWeekRef.value) : null;
});

const isPrevDisabled = computed(() => {
  if (currentlySelectedWeekNumeric.value === null) return true;
  const index = allPossibleWeeksSorted.value.indexOf(currentlySelectedWeekNumeric.value);
  return index <= 0;
});

const isNextDisabled = computed(() => {
  if (currentlySelectedWeekNumeric.value === null) return true;
  const index = allPossibleWeeksSorted.value.indexOf(currentlySelectedWeekNumeric.value);
  return index === -1 || index >= allPossibleWeeksSorted.value.length - 1;
});

const navigateToPreviousWeek = () => {
  if (isPrevDisabled.value || currentlySelectedWeekNumeric.value === null) return;
  
  const index = allPossibleWeeksSorted.value.indexOf(currentlySelectedWeekNumeric.value);
  if (index > 0) {
    const prevWeek = allPossibleWeeksSorted.value[index - 1];
    const prevWeekStr = prevWeek.toString();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({ query: { ...route.query, week: prevWeekStr } });
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleWeekSelect(prevWeekStr);
  }
};

const navigateToNextWeek = () => {
  if (isNextDisabled.value || currentlySelectedWeekNumeric.value === null) return;
  
  const index = allPossibleWeeksSorted.value.indexOf(currentlySelectedWeekNumeric.value);
  if (index < allPossibleWeeksSorted.value.length - 1) {
    const nextWeek = allPossibleWeeksSorted.value[index + 1];
    const nextWeekStr = nextWeek.toString();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({ query: { ...route.query, week: nextWeekStr } });
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleWeekSelect(nextWeekStr);
  }
};
</script>

<style scoped>
</style> 